import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, ShieldCheck, ExternalLink, Clock } from 'lucide-react';
import { formatDate } from '../utils/timeFormat';

/**
 * Iter-219 — Contract Story 3 — Agreement Gate modal.
 *
 * Mounted once in App.js. Listens to a `window` event dispatched by
 * `utils/api.js` interceptor whenever the backend returns 409
 * `{detail: "agreement_required", ...}` OR a non-409 response carrying
 * the `X-Agreement-Warning` JSON header (soft_grace mode).
 *
 * The modal shows the agreement title/version/effective date + a reason
 * for the block, and routes the user into `/contracts/pending` via a
 * single-click "Review & Sign" CTA. Drafts in localStorage are
 * preserved — we never auto-cancel work; we either redirect the user or
 * close the modal for soft_grace.
 */

/* Iter-225b — delegate to shared MM-DD-YYYY formatter from utils/timeFormat. */

const REASON_BY_MODE = {
  hard_block:   'Your account requires an updated agreement before you can continue. Review and sign to unlock this action.',
  force_resign: 'A new version of your agreement is in effect. Please review and re-sign to continue using the platform.',
  soft_grace:   'A new agreement is awaiting your signature. You can continue for now, but please sign it soon to avoid interruption.',
};

const AgreementGateModal = () => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);

  const close = useCallback(() => setPayload(null), []);

  useEffect(() => {
    const handler = (ev) => {
      const next = ev.detail;
      if (!next || !next.agreements?.length) return;
      // If a hard_block is already showing, don't override with a softer one.
      setPayload((cur) => {
        if (!cur) return next;
        if (cur.mode === 'force_resign') return cur;
        if (cur.mode === 'hard_block' && next.mode === 'soft_grace') return cur;
        return next;
      });
    };
    window.addEventListener('agreement-gate', handler);
    return () => window.removeEventListener('agreement-gate', handler);
  }, []);

  if (!payload) return null;

  const mode = payload.mode || 'hard_block';
  const blocking = mode === 'hard_block' || mode === 'force_resign';
  const top = payload.agreements?.[0] || {};
  const more = (payload.agreements?.length || 0) - 1;
  const reason = REASON_BY_MODE[mode] || REASON_BY_MODE.hard_block;

  const reviewNow = () => {
    // Iter-298 — route to the role-scoped contracts page. Previously
    // we sent the user to `/contracts/pending` which doesn't exist as
    // a top-level route in App.js, so they hit Page Not Found and
    // could never finish signing the Service Agreement.
    let target = '/talent/contracts';
    try {
      const raw = localStorage.getItem('user');
      const u = raw ? JSON.parse(raw) : null;
      const role = (u?.role || '').toLowerCase();
      if (role === 'client' || role === 'agency') target = '/client/contracts';
      else if (role === 'admin' || role === 'super_admin') target = '/admin/contracts';
      else if (role === 'talent' || role === 'ai_creator') target = '/talent/contracts';
    } catch { /* fall back to talent/contracts */ }
    close();
    navigate(target);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !blocking) close(); }}>
      <DialogContent
        className="max-w-md sm:max-w-lg"
        data-testid="agreement-gate-modal"
        onPointerDownOutside={(e) => { if (blocking) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (blocking) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {blocking
              ? <AlertTriangle className="w-5 h-5 text-rose-600" />
              : <ShieldCheck className="w-5 h-5 text-amber-600" />}
            {mode === 'force_resign' ? 'Re-sign required' :
             mode === 'soft_grace'   ? 'Agreement update available' :
                                       'Agreement required'}
          </DialogTitle>
          <DialogDescription>{reason}</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2" data-testid="agreement-gate-top-agreement">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate" data-testid="agreement-gate-title">
                {top.title || 'Agreement'}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {top.audience && (
                  <Badge variant="outline" className="text-[11px]">{top.audience}</Badge>
                )}
                {top.version && (
                  <Badge variant="outline" className="text-[11px]" data-testid="agreement-gate-version">
                    v{top.version}
                  </Badge>
                )}
                {top.jurisdiction && top.jurisdiction !== 'ALL' && (
                  <Badge variant="outline" className="text-[11px]">{top.jurisdiction}</Badge>
                )}
                {top.supersedes_id && (
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[11px]">
                    new version
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {top.effective_at && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Effective {formatDate(top.effective_at)}
            </p>
          )}
        </div>

        {more > 0 && (
          <p className="text-xs text-gray-500" data-testid="agreement-gate-more-count">
            +{more} other agreement{more > 1 ? 's' : ''} waiting your signature.
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {!blocking && (
            <Button variant="outline" onClick={close} data-testid="agreement-gate-maybe-later">
              Maybe later
            </Button>
          )}
          <Button
            onClick={reviewNow}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="agreement-gate-review"
          >
            Review &amp; Sign <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgreementGateModal;
